import React, { useState } from 'react';
import {
  X,
  BookOpen,
  User,
  Calendar,
  Users,
  DollarSign,
  FileText,
  Edit,
  Save
} from 'lucide-react';
import Swal from 'sweetalert2';
import api from '../api';

const FormationDetail = ({ formation, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    titre: formation.titre || '',
    description: formation.description || '',
    isPaid: formation.isPaid || false,
    prix: formation.prix || 0
  });
  const [updating, setUpdating] = useState(false);

  if (!formation) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'Gratuit';
    return `${parseFloat(price).toLocaleString('fr-FR')} FCFA`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setUpdating(true);
      const response = await api.patch(`/api/formations/admin/formations/${formation.id}/`, {
        ...formData,
        auteur_id: formation.auteur.id
      });

      Swal.fire({
        title: 'Modifiée !',
        text: 'La formation a été modifiée avec succès.',
        icon: 'success',
        confirmButtonColor: '#FF6B00',
        confirmButtonText: 'OK',
        timer: 2000,
        customClass: {
          popup: 'rounded-xl',
          confirmButton: 'rounded-lg px-6 py-2'
        }
      });

      if (onUpdate) {
        onUpdate(response.data);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      Swal.fire({
        title: 'Erreur !',
        text: 'Erreur lors de la modification de la formation.',
        icon: 'error',
        confirmButtonColor: '#FF6B00',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-xl',
          confirmButton: 'rounded-lg px-6 py-2'
        }
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-[#FF6B00] to-[#437011]">
              <div className="flex items-center gap-3">
                <BookOpen size={28} className="text-white" />
                <div>
                  <h2 className="text-2xl font-bold text-white">Détails de la formation</h2>
                  <p className="text-white text-sm opacity-90">{formation.titre}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => setActiveTab('info')}
                className={`flex-1 px-6 py-3 font-medium transition-colors ${
                  activeTab === 'info'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Informations
              </button>
              <button
                onClick={() => setActiveTab('participants')}
                className={`flex-1 px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'participants'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Users size={18} />
                Participants ({formation.participants_count || 0})
              </button>
              <button
                onClick={() => setActiveTab('resources')}
                className={`flex-1 px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'resources'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText size={18} />
                Ressources
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'info' && (
                <div className="space-y-6">
                  {/* Image de couverture */}
                  {formation.couverture && (
                    <div className="relative h-64 rounded-lg overflow-hidden">
                      <img
                        src={`http://192.168.1.65:8000${formation.couverture}`}
                        alt={formation.titre}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Titre */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titre</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.titre}
                        onChange={(e) => handleInputChange('titre', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                      />
                    ) : (
                      <p className="text-lg font-semibold text-gray-900">{formation.titre}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    {isEditing ? (
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent resize-none"
                      />
                    ) : (
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {formation.description || 'Aucune description'}
                      </p>
                    )}
                  </div>

                  {/* Type et Prix */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                      {isEditing ? (
                        <select
                          value={formData.isPaid}
                          onChange={(e) => handleInputChange('isPaid', e.target.value === 'true')}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                        >
                          <option value="false">Gratuite</option>
                          <option value="true">Payante</option>
                        </select>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          formation.isPaid
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {formation.isPaid ? 'Payante' : 'Gratuite'}
                        </span>
                      )}
                    </div>

                    {(isEditing ? formData.isPaid : formation.isPaid) && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Prix</label>
                        {isEditing ? (
                          <input
                            type="number"
                            value={formData.prix}
                            onChange={(e) => handleInputChange('prix', parseFloat(e.target.value) || 0)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                          />
                        ) : (
                          <p className="text-lg font-semibold text-green-700">
                            {formatPrice(formation.prix)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Auteur */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <User size={18} className="text-[#FF6B00]" />
                      <h3 className="font-semibold text-gray-900">Auteur</h3>
                    </div>
                    <div className="flex items-center gap-3 ml-7">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                        {formation.auteur?.first_name?.[0] || formation.auteur?.username?.[0] || 'A'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {formation.auteur?.first_name && formation.auteur?.last_name
                            ? `${formation.auteur.first_name} ${formation.auteur.last_name}`
                            : formation.auteur?.username || 'Auteur'}
                        </p>
                        <p className="text-sm text-gray-600">{formation.auteur?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={18} className="text-blue-600" />
                        <h3 className="font-semibold text-blue-900">Créée le</h3>
                      </div>
                      <p className="text-blue-700 ml-7">{formatDate(formation.created_at)}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={18} className="text-purple-600" />
                        <h3 className="font-semibold text-purple-900">Mise à jour le</h3>
                      </div>
                      <p className="text-purple-700 ml-7">{formatDate(formation.updated_at)}</p>
                    </div>
                  </div>

                  {/* Statistiques */}
                  <div className="bg-gradient-to-r from-[#FF6B00] to-[#437011] rounded-lg p-6 text-white">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Users size={20} />
                      Participants
                    </h3>
                    <p className="text-4xl font-bold">{formation.participants_count || 0}</p>
                  </div>
                </div>
              )}

              {activeTab === 'participants' && (
                <div className="text-center py-12">
                  <Users size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Liste des participants à implémenter</p>
                </div>
              )}

              {activeTab === 'resources' && (
                <div className="text-center py-12">
                  <FileText size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Liste des ressources à implémenter</p>
                </div>
              )}
            </div>

            {/* Footer - Actions */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        titre: formation.titre || '',
                        description: formation.description || '',
                        isPaid: formation.isPaid || false,
                        prix: formation.prix || 0
                      });
                    }}
                    className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updating}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-[#FF6B00] to-[#e55d00] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save size={18} />
                    {updating ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#FF6B00] to-[#e55d00] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Edit size={18} />
                  Modifier
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormationDetail;
